import { useAuthStore } from '@/sync/auth'
import { useSettingsStore } from '@/stores/settings'
import { getSupabase } from '@/sync/supabase'
import { db } from '@/db/schema'
import { createId } from '@/utils/id'

/**
 * Pipeline de eventos para estadísticas globales (panel admin).
 *
 * Offline-first: los eventos se encolan en IndexedDB (`eventQueue`) y un
 * flush periódico (cada 30 s o N eventos) los envía a `app_events` en
 * batches. Si no hay sesión o Supabase no está configurado, se quedan en
 * la cola hasta que las condiciones se cumplan.
 *
 * Privacidad: NUNCA meter en `props` contenido sensible (texto de libros,
 * highlights, contraseñas, claves). Sólo métricas: IDs, contadores, duraciones.
 */

export interface QueuedEvent {
  /** ID local de la cola (no se envía al backend). */
  id: string
  type: string
  props: Record<string, unknown>
  queuedAt: number
  /** Veces que se ha intentado enviar sin éxito. */
  attempts: number
}

const FLUSH_INTERVAL_MS = 30_000
const FLUSH_BATCH_SIZE = 50
const MAX_ATTEMPTS = 5

let flushTimer: ReturnType<typeof setInterval> | null = null
let started = false

/**
 * Encola un evento para enviar al backend. No bloquea: si la BD local
 * falla, el evento se pierde silenciosamente (mejor que romper la UX).
 *
 * @example
 * track('reading_session', { bookId, seconds: 600, percentEnd: 42 })
 */
export async function track(type: string, props: Record<string, unknown> = {}): Promise<void> {
  try {
    await db.eventQueue.add({
      id: createId(),
      type,
      props,
      queuedAt: Date.now(),
      attempts: 0,
    })
  } catch {
    // silencioso: las métricas no deben romper la app
  }
  // Si la cola crece mucho, forzar un flush inmediato.
  try {
    const count = await db.eventQueue.count()
    if (count >= FLUSH_BATCH_SIZE) void flushEvents()
  } catch {
    /* noop */
  }
}

/**
 * Envía los eventos encolados a `app_events`. Best-effort: descarta los
 * que superen MAX_ATTEMPTS tras varios reintentos.
 */
export async function flushEvents(): Promise<void> {
  const auth = useAuthStore()
  const settings = useSettingsStore()
  const userId = auth.userId
  if (!userId) return // sin sesión, no tiene sentido flushar
  const client = getSupabase(settings.settings.sync)
  if (!client) return

  let pending: QueuedEvent[]
  try {
    pending = await db.eventQueue.orderBy('queuedAt').limit(FLUSH_BATCH_SIZE).toArray()
  } catch {
    return
  }
  if (pending.length === 0) return

  const rows = pending.map((e) => ({
    user_id: userId,
    type: e.type,
    props: e.props,
    // occurred_at: ahora (no queuedAt, que puede ser antiguo en offline)
    occurred_at: new Date().toISOString(),
  }))

  try {
    const { error } = await client.from('app_events').insert(rows)
    if (error) throw error
    // Éxito: borrar de la cola.
    await db.eventQueue.bulkDelete(pending.map((e) => e.id))
  } catch {
    // Fallo: incrementar attempts y descartar los crónicos.
    await Promise.all(
      pending.map(async (e) => {
        if (e.attempts + 1 >= MAX_ATTEMPTS) {
          await db.eventQueue.delete(e.id)
        } else {
          await db.eventQueue.update(e.id, { attempts: e.attempts + 1 })
        }
      }),
    )
  }
}

/**
 * Arranca el flusher periódico. Idempotente: llamar varias veces no crea
 * múltiples intervalos. Lo invoca `main.ts` al arrancar la app.
 */
export function startEventTracker(): void {
  if (started) return
  started = true
  if (flushTimer) clearInterval(flushTimer)
  flushTimer = setInterval(() => {
    void flushEvents()
  }, FLUSH_INTERVAL_MS)
  // También flushear al volver online (si estuvimos offline).
  window.addEventListener('online', () => void flushEvents())
  // Y al cerrar/recargar, intentar un último flush.
  window.addEventListener('beforeunload', () => void flushEvents())
}

export function useEventTracker() {
  return { track, flushEvents, startEventTracker }
}
